import { FC, useEffect, useRef, useState } from 'react'
import {
	Button,
	Card,
	Checkbox,
	Form,
	Input,
	Progress,
	Select,
	Space,
	Steps,
	Tag,
	Typography,
	Upload,
	message
} from 'antd'
import {
	CheckCircleOutlined,
	InboxOutlined,
	UploadOutlined
} from '@ant-design/icons'
import {
	CarrierType,
	UploadResponse,
	fetchCarrierTypes,
	registerVersion,
	uploadBinary
} from '../api/updates'

const { Title, Text } = Typography
const { Dragger } = Upload
const { TextArea } = Input

const UpdatesDeploy: FC = () => {
	const [step, setStep] = useState(0)
	const [carrierTypes, setCarrierTypes] = useState<CarrierType[]>([])
	const [uploadResult, setUploadResult] =
		useState<UploadResponse | null>(null)
	const [uploadProgress, setUploadProgress] = useState(0)
	const [uploading, setUploading] = useState(false)
	const [registering, setRegistering] = useState(false)
	const [done, setDone] = useState(false)
	const [selectedFile, setSelectedFile] = useState<File | null>(null)
	const [form] = Form.useForm()
	const fileRef = useRef<File | null>(null)

	useEffect(() => {
		fetchCarrierTypes()
			.then(setCarrierTypes)
			.catch(() => message.error('Failed to load carrier types'))
	}, [])

	const handleUpload = async () => {
		if (!selectedFile) {
			message.warning('Select a binary file first')
			return
		}
		setUploading(true)
		setUploadProgress(0)
		try {
			const result = await uploadBinary(selectedFile, (pct) =>
				setUploadProgress(pct)
			)
			setUploadResult(result)
			form.setFieldsValue({
				filename: result.filename,
				checksum: result.checksum,
				size: result.size
			})
			setStep(1)
		} catch {
			message.error('Upload failed')
		} finally {
			setUploading(false)
		}
	}

	const handleRegister = async (values: {
		carrierType: string
		version: string
		releaseNotes?: string
		setAsLatest: boolean
	}) => {
		if (!uploadResult) return
		setRegistering(true)
		try {
			await registerVersion({
				...values,
				filename: uploadResult.filename,
				checksum: uploadResult.checksum,
				size: uploadResult.size
			})
			setDone(true)
			setStep(2)
		} catch {
			message.error('Failed to register version')
		} finally {
			setRegistering(false)
		}
	}

	const reset = () => {
		setStep(0)
		setUploadResult(null)
		setUploadProgress(0)
		setSelectedFile(null)
		setDone(false)
		form.resetFields()
	}

	return (
		<div className="carriers">
			<header className="page-header">
				<Title level={4} style={{ margin: 0 }}>
					Deploy New Version
				</Title>
			</header>

			<main>
				<Steps
					current={step}
					style={{ marginBottom: 32, maxWidth: 600 }}
					items={[
						{ title: 'Upload Binary' },
						{ title: 'Register Metadata' },
						{ title: 'Done' }
					]}
				/>

				{step === 0 && (
					<Card style={{ maxWidth: 600 }}>
						<Title level={5}>Step 1 — Upload the binary file</Title>
						<Dragger
							beforeUpload={(file) => {
								setSelectedFile(file)
								fileRef.current = file
								return false
							}}
							onRemove={() => {
								setSelectedFile(null)
								fileRef.current = null
							}}
							maxCount={1}
							accept=".bin,.exe,.AppImage,.deb,.rpm,.tar.gz,.gz,.zip">
							<p className="ant-upload-drag-icon">
								<InboxOutlined />
							</p>
							<p className="ant-upload-text">
								Click or drag a binary file to upload
							</p>
							<p className="ant-upload-hint">
								Supports any binary format. SHA-256 checksum is
								computed server-side.
							</p>
						</Dragger>

						{uploading && (
							<Progress
								percent={uploadProgress}
								style={{ marginTop: 16 }}
							/>
						)}

						<Button
							type="primary"
							icon={<UploadOutlined />}
							onClick={handleUpload}
							loading={uploading}
							disabled={!selectedFile}
							style={{ marginTop: 16 }}>
							Upload
						</Button>
					</Card>
				)}

				{step === 1 && uploadResult && (
					<Card style={{ maxWidth: 600 }}>
						<Title level={5}>Step 2 — Register metadata</Title>
						<Space
							direction="vertical"
							size={4}
							style={{ marginBottom: 20 }}>
							<Text type="secondary">
								Filename:{' '}
								<Tag>{uploadResult.filename}</Tag>
							</Text>
							<Text type="secondary">
								Checksum:{' '}
								<Text code style={{ fontSize: 12 }}>
									{uploadResult.checksum}
								</Text>
							</Text>
							<Text type="secondary">
								Size:{' '}
								<strong>
									{(uploadResult.size / 1024 / 1024).toFixed(2)}{' '}
									MB
								</strong>
							</Text>
						</Space>

						<Form
							form={form}
							layout="vertical"
							onFinish={handleRegister}
							initialValues={{ setAsLatest: true }}>
							<Form.Item
								label="Carrier Type"
								name="carrierType"
								rules={[
									{
										required: true,
										message: 'Select a carrier type'
									}
								]}>
								<Select
									placeholder="Select carrier type"
									options={carrierTypes.map((ct) => ({
										value: ct.slug,
										label: `${ct.name} (${ct.slug})`
									}))}
								/>
							</Form.Item>

							<Form.Item
								label="Version"
								name="version"
								rules={[
									{
										required: true,
										message: 'Version is required'
									},
									{
										pattern: /^\d+\.\d+\.\d+/,
										message: 'Use semver format, e.g. 1.2.3'
									}
								]}>
								<Input placeholder="e.g. 1.2.3" />
							</Form.Item>

							<Form.Item
								label="Release Notes"
								name="releaseNotes">
								<TextArea
									rows={3}
									placeholder="Optional release notes..."
								/>
							</Form.Item>

							<Form.Item
								name="setAsLatest"
								valuePropName="checked">
								<Checkbox>
									Set as latest — machines start receiving this
									version on their next check-in
								</Checkbox>
							</Form.Item>

							<Space>
								<Button onClick={reset}>Back</Button>
								<Button
									type="primary"
									htmlType="submit"
									loading={registering}>
									Register Version
								</Button>
							</Space>
						</Form>
					</Card>
				)}

				{step === 2 && done && (
					<Card style={{ maxWidth: 600, textAlign: 'center' }}>
						<CheckCircleOutlined
							style={{
								fontSize: 48,
								color: '#52c41a',
								marginBottom: 16
							}}
						/>
						<Title level={4}>Version deployed successfully</Title>
						<Text type="secondary">
							The version has been registered. Machines assigned to the
							selected carrier type will download it on their next check-in
							(up to 30 minutes).
						</Text>
						<div style={{ marginTop: 24 }}>
							<Button type="primary" onClick={reset}>
								Deploy Another Version
							</Button>
						</div>
					</Card>
				)}
			</main>
		</div>
	)
}

export default UpdatesDeploy
